import _get from "lodash/get";
import { hasExternalLegalOwner } from "./accountSelectors";
import { getActiveSubscription } from "./cartSelectors";
import { getCheckout, State as CheckoutState } from "./checkout";
import * as COMBINED_SELECTORS from "./combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "./customerInfoSelectors";
import { getPhoneNumberCheckIsAllowedToProceed } from "./phoneNumberSelectors";
import { getLegalSubscriptionId, isReSwitch as isReSwitchSelector, hasSubscriptions } from "./subscriptionsSelectors";
import * as VERIFICAITON_SELECTORS from "./verificationSelectors";
import { CUSTOMER_TYPE, CustomerType, FORM_ID, FormId } from "../../../../../constants";
import { Form, RootState } from "../../../../../models";
import { getBirthDateFromSSN } from "../../../app/utils";
import {
    isBankIdVerified as isBankIdVerifiedSelector,
    isBankIdAvailable as isBankIdAvailableSelector,
    State as AuthState,
} from "../../../authentication/state/selectors";
import { VALIDATION_FIELD } from "../../../rapidform/formValidation";

export type State = CheckoutState & AuthState;

// @ts-expect-error: TODO: handle not found forms
export const getFormById = (state: State, id: FormId): Form => _get(getCheckout(state), id, {});

// Personal Details Form
export const showLegalOwnerFields = (state: State) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const isExistingMissingEmail = CUSTOMER_INFO_SELECTORS.getCurrentIsExistingMissingEmail(state);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    const isBankIdAvailable = isBankIdAvailableSelector(state);
    const isExisting = CUSTOMER_TYPE.EXISTING === customerType;
    const isPrepaidAndContainsPostpaid = CUSTOMER_INFO_SELECTORS.getCurrentIsPrepaidAndContainsPostpaid(state);
    const hasLegalOwnerFields =
        ([CUSTOMER_TYPE.PORT_IN, CUSTOMER_TYPE.NEW] as (CustomerType | undefined)[]).includes(customerType) ||
        (isExisting && (isPrepaidAndContainsPostpaid || isExistingMissingEmail));
    return hasLegalOwnerFields && (isBankIdVerified || !isBankIdAvailable);
};

export const getLegalOwnerBirthDate = (state: State) => {
    const { values } = getFormById(state, FORM_ID.PERSONAL_DETAILS);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    const isExisting = CUSTOMER_INFO_SELECTORS.getCurrentType(state) === CUSTOMER_TYPE.EXISTING;
    let legalOwnerBirthDate = _get(values, `legalOwner.${VALIDATION_FIELD.BIRTHDATE}`, null);

    if (!isBankIdVerified && !isExisting) {
        legalOwnerBirthDate = getBirthDateFromSSN(_get(values, `legalOwner.${VALIDATION_FIELD.SSN}`, null));
    }

    return legalOwnerBirthDate;
};

// Personal Details Continue Button
export const existingVerified = (state: State) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const isExistingOrUndefined = (
        [CUSTOMER_TYPE.EXISTING, null, undefined] as (CustomerType | null | undefined)[]
    ).includes(customerType);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    const isBankIdAvailable = isBankIdAvailableSelector(state);
    const value =
        isExistingOrUndefined &&
        (isBankIdAvailable ? isBankIdVerified : VERIFICAITON_SELECTORS.canProductUserProceed(state));

    return value;
};

export const newOrPortInNotBankIdVerified = (state: State) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const newOrPortin = (
        [CUSTOMER_TYPE.NEW, CUSTOMER_TYPE.PORT_IN, CUSTOMER_TYPE.NEW_OR_PORT_IN] as (CustomerType | undefined)[]
    ).includes(customerType);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    const isBankIdAvailable = isBankIdAvailableSelector(state);
    return newOrPortin && !(isBankIdVerified || !isBankIdAvailable);
};
export const newOrPortInNotCreditChecked = (state: State) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const newOrPortin = (
        [CUSTOMER_TYPE.NEW, CUSTOMER_TYPE.PORT_IN, CUSTOMER_TYPE.NEW_OR_PORT_IN] as (CustomerType | undefined)[]
    ).includes(customerType);
    const isCreditCheckOk =
        VERIFICAITON_SELECTORS.isAllowedToProceed(state) &&
        VERIFICAITON_SELECTORS.isCreditCheckRequired(state) &&
        VERIFICAITON_SELECTORS.isCreditCheckPerformed(state);
    return newOrPortin && !isCreditCheckOk;
};
export const existingRequiringCreditCheckNotBankIdVerified = (state: State) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const existing = ([CUSTOMER_TYPE.EXISTING] as (CustomerType | undefined)[]).includes(customerType);
    // TODO: MS fix requiresCreditcheck flow
    const creditCheckRequired = VERIFICAITON_SELECTORS.isCreditCheckRequired(state);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    const isBankIdAvailable = isBankIdAvailableSelector(state);
    return existing && creditCheckRequired && !(isBankIdVerified || !isBankIdAvailable);
};
export const reSwitchNotBankIdVerified = (state: State) => {
    const legalSubscriptionId = getLegalSubscriptionId(state);
    const isReSwitch = isReSwitchSelector(state, legalSubscriptionId);
    const isBankIdVerified = isBankIdVerifiedSelector(state);
    return isReSwitch && !isBankIdVerified;
};
export const canContinuePersonalDetails = (state: RootState) => {
    const customerType = CUSTOMER_INFO_SELECTORS.getCurrentType(state);
    const isExistingOrUndefined = (
        [CUSTOMER_TYPE.EXISTING, null, undefined] as (CustomerType | null | undefined)[]
    ).includes(customerType);

    return (
        !(
            (!existingVerified(state) && isExistingOrUndefined) ||
            newOrPortInNotBankIdVerified(state) ||
            // TODO: Can we skip this? On navigating to member we call checkout.checkCustomerAndSetPersonalDetails, which will check/do creditcheck if needed
            // newOrPortInNotCreditChecked(state) ||
            existingRequiringCreditCheckNotBankIdVerified(state) ||
            reSwitchNotBankIdVerified(state)
        ) ||
        (COMBINED_SELECTORS.isHardwareOnly(state) && !COMBINED_SELECTORS.isHwOnlyButRequireBankid(state)) ||
        (COMBINED_SELECTORS.isHwOnlyButRequireBankid(state) && isBankIdVerifiedSelector(state)) ||
        // Only set restriction on external if subscriptions in cart
        (hasExternalLegalOwner(state) && !hasSubscriptions(state))
    );
};

// Add Member Form
export const showAddMemberInputFields = (state: State) => {
    const activeSubscription = getActiveSubscription(state);
    const memberForm = getFormById(state, FORM_ID.ADD_MEMBER);

    const customerType = _get(memberForm, "values.member.type", null);
    const phoneNumberCheckIsAllowedToProceed = getPhoneNumberCheckIsAllowedToProceed(state, activeSubscription);

    return (!!activeSubscription && phoneNumberCheckIsAllowedToProceed) || customerType === CUSTOMER_TYPE.NEW;
};
